import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase, SESSION_DURATION_SECONDS } from "../lib/supabase.js";

/**
 * Clears ALL Supabase session keys from localStorage.
 *
 * In v1.23.0 we set storageKey: "qm-auth-token". Supabase JS v2 falls back to
 * reading the old default key ("sb-<project-ref>-auth-token") when the configured
 * key is empty, so old sessions are found and the user appears logged in.
 * However, supabase.auth.signOut() only clears the CONFIGURED key — the old key
 * survives, and on the next page load the old session is restored. The user
 * cannot log out.
 *
 * This helper clears both the current key and any legacy Supabase keys so that
 * logout is always complete, regardless of which key the token was stored under.
 */
function purgeAllSupabaseSessions() {
  localStorage.removeItem("qm-auth-token");
  Object.keys(localStorage)
    .filter(k => k.startsWith("sb-") && k.endsWith("-auth-token"))
    .forEach(k => localStorage.removeItem(k));
}

/**
 * AuthContext — provides Supabase Auth session to the entire app.
 *
 * Replaces the legacy phone-in-localStorage pattern with JWT-based auth.
 * The access token is kept in memory (not localStorage) for security.
 *
 * Usage:
 *   <AuthProvider><App /></AuthProvider>
 *
 *   const { user, session, accessToken, signOut, loading } = useAuth();
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session — sign out automatically if older than SESSION_DURATION_SECONDS (7 days)
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      if (s) {
        const createdAt = s.user?.created_at ? new Date(s.user.created_at).getTime() : 0;
        const lastSignIn = s.user?.last_sign_in_at ? new Date(s.user.last_sign_in_at).getTime() : createdAt;
        const sessionAge = (Date.now() - lastSignIn) / 1000;
        if (sessionAge > SESSION_DURATION_SECONDS) {
          try { await supabase.auth.signOut(); } catch { /* best-effort — purge clears storage below */ }
          purgeAllSupabaseSessions();
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }
      }
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
    });

    // Listen for auth state changes (login, logout, token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = useCallback(async () => {
    try { await supabase.auth.signOut(); } catch { /* best-effort */ }
    purgeAllSupabaseSessions(); // always clear all keys, including legacy sb-*-auth-token
    setSession(null);
    setUser(null);
  }, []);

  const value = {
    session,
    user,
    /** The JWT access token — attach to API requests as Bearer token */
    accessToken: session?.access_token ?? null,
    /** User's phone from Supabase Auth (if authenticated via phone OTP) */
    phone: user?.phone ?? null,
    /** User's email from Supabase Auth (if authenticated via email) */
    email: user?.email ?? null,
    /** User's role from metadata (until DB role lookup is wired) */
    role: user?.user_metadata?.role ?? "citizen",
    loading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth state.
 * Must be used within <AuthProvider>.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

/**
 * Module-level getter for the current access token.
 * Used by apiFetch() which can't use hooks.
 *
 * Returns null if not authenticated.
 *
 * Hotfix v1.27.2 — close the silent-logout race.
 *
 * Before this, `_currentAccessToken` was seeded as `null` and populated
 * asynchronously by `supabase.auth.getSession()`. Any apiFetch() that ran
 * between module load and the promise resolving saw `null` and sent the
 * request WITHOUT an Authorization header → server returned 401 →
 * apiFetch's 401 handler ran clearAllClientAuthSync() → session was
 * genuinely wiped → next refresh showed the login screen.
 *
 * Reproduction was a browser reload while a voter was logged in:
 *   1. AuthContext loads, _currentAccessToken = null, getSession() pending.
 *   2. React mounts App → useAppState hydrates `voter` from localStorage
 *      synchronously (lsGet("qm_voter")).
 *   3. useEffect on voter.phone/email fires getXpState() immediately.
 *   4. apiFetch sees _currentAccessToken === null → no Auth header.
 *   5. 401. clearAllClientAuthSync fires. Session is now actually dead.
 *
 * Fix: expose an `authReady` promise that resolves as soon as the Supabase
 * SDK finishes its initial session restore. apiFetch awaits it before the
 * first network call. When the session is already in memory (warm cache)
 * the await is a single microtask. When it isn't (cold reload), apiFetch
 * waits the tiny number of ms it takes to read + parse localStorage.
 */
let _currentAccessToken = null;

let _resolveAuthReady;
/**
 * Resolves the moment the Supabase SDK has finished its initial session
 * read — either via the `getSession()` promise below OR the first
 * `onAuthStateChange` callback, whichever fires first. apiFetch awaits
 * this before reading the token so the race described above can't happen.
 *
 * Never rejects. Safe to await multiple times (always the same resolution).
 */
export const authReady = new Promise((resolve) => {
  _resolveAuthReady = resolve;
});

let _authReadyResolved = false;
function markAuthReady() {
  if (_authReadyResolved) return;
  _authReadyResolved = true;
  _resolveAuthReady();
}

// Keep in sync with AuthContext via a listener. The first call to this
// (typically the SDK's synthetic INITIAL_SESSION event) also satisfies
// authReady — it means the SDK has finished reading localStorage.
supabase.auth.onAuthStateChange((_event, session) => {
  _currentAccessToken = session?.access_token ?? null;
  markAuthReady();
});

// Also initialise from existing session. Either this or the listener
// above wins — whichever resolves first flips authReady.
supabase.auth.getSession().then(({ data: { session } }) => {
  _currentAccessToken = session?.access_token ?? null;
  markAuthReady();
});

// Belt-and-braces: if Supabase somehow never fires its initial events
// (SDK regression, broken localStorage, test environment without the
// SDK mocked properly), unblock callers after 5 s rather than hang the
// whole UI. The token stays null in that case — apiFetch will hit 401
// cleanly instead of deadlocking awaiting authReady forever.
if (typeof setTimeout !== "undefined") {
  setTimeout(markAuthReady, 5000);
}

export function getAccessToken() {
  return _currentAccessToken;
}
